import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, Select, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import CloseLine from '@/assets/images/icons/close-line.react.svg?react';
import CreateTapdEditDisable from '@/assets/images/public/create-tapd-edit-disable.svg';
import CreateTapdEdit from '@/assets/images/public/create-tapd-edit.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { storageSet } from '@/infrastructure/storage-utils';
import { ModalIconType, ModalMsg, ModalType } from '@/presentation/components/modal-msg';
import { getInitFields, initReporterData } from '@/presentation/components/tapd';
import { getTapdTitleMethodBatch } from '@/presentation/components/tapd-info';
import {
  getCustomFields,
  getIssueTapdInfos,
  deleteIssueTapdInfos, getLastCrashStackNew, createTapdIssue,
} from '@/presentation/components/tapd/api';
import {
  getLocalTapd,
  FieldsType,
  formatFieldsValue,
  CustomFieldsType,
  getTapdTitleKey,
  getTapdStatusName,
  StatusMap,
  formatCustomFields,
} from '@/presentation/components/tapd/create-tapd-modal';
import { RenderAutoComplete } from '@/presentation/components/tapd/create-tapd-modal/auto-complete';
import styles from '@/presentation/components/tapd/create-tapd-modal/index.module.scss';
import { RenderInput } from '@/presentation/components/tapd/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd/create-tapd-modal/select';
import { RenderUserSelect } from '@/presentation/components/tapd/create-tapd-modal/user-select';
import { EditType } from '@/presentation/components/tapd/edit-tapd-modal';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import { useStores } from '@/presentation/store';
import { FilterOptions } from '@/proto/alert';
import { Zone, ExOption, Option } from '@/proto/field';
import { getTypeByZone, IssueType } from '@/proto/issue';
import { IStatusLinkageRule, ITapdTemplateInfos } from '@/proto/manage';
import { useTranslation } from 'react-i18next';

type IProps<T> = {
  appVersionOptions: Option[] | ExOption[],
  visible: boolean,
  onHide: () => void,
  rowsData: T[],
  type: Zone,
  onSubmit: () => void;
  getIssueUrl: (data: T) => string;
  onClose: () => void;
  openEidtModal: (eaitType: EditType, mode: string) => void;
  onChangeId: (workspaceId: string) => void;
  pageId: string;
  issueType?: IssueType;
};
const SelectOption = Select.Option;
export const BatchCreateTapdModal = <T extends Record<string, any>>(props: IProps<T>) => {
  const { t } = useTranslation();
  const {
    appVersionOptions,
    visible,
    onHide,
    rowsData,
    type,
    onSubmit,
    getIssueUrl,
    onClose,
    openEidtModal,
    onChangeId,
    pageId,
  } = props;
  const { tapdList } = useStores().TapdStore;
  const [tapdLists, setTapdLists] = useState<FilterOptions[]>([]);
  const [loading, setLoading] = useState(false);
  const initTitlePrefix = t("【Bugly-质量监控】");
  const [tapdTitlePrefix, setTapdTitlePrefix] = useState('');
  const [fields, setFields] = useState<FieldsType[]>(getInitFields());
  const [params, setParams] = useState({
    work_space_id: '',
  });
  const [relateStatus, setRelateStatus] = useState<IStatusLinkageRule[]>([]);
  const [statusFields, setStatusFields] = useState<FieldsType>();
  const [tapdTemplateInfos, setTapdTemplateInfos] = useState<ITapdTemplateInfos[]>([]);
  const [mode, setMode] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const handleSelectChange = (val: string | number, key?: string) => {
    setParams({ ...params, [key as string]: val });
    onChangeId(val as string);
  };

  const handleChangeMode = (val: string | number) => setMode(val as string);

  const loadIsAdmin = async () => {
    setLoading(true);
    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
    setLoading(false);
  };

  const batchCreateTapd = async (rows: T) => {
    const exType = getTypeByZone(type);
    const issueId = rows.issueId || rows.feature || rows.clientIdentify;
    let exceptionName = getTapdTitleMethodBatch(type, rows);
    let keyMethods: string[] = rows?.keyMethodList ?? rows.keyMethods ?? [];

    if (exType === Zone.Exception) {
      const issueInfo = await getLastCrashStackNew(
        rows.lastCrashUUID,
        props.issueType as IssueType,
        rows.issueId,
      );
      exceptionName = rows?.issueFeature?.exceptionName;
      keyMethods = issueInfo.detail.split('\n')?.filter(item => !!item);
    }

    await createTapdIssue({
      workSpaceId: params.work_space_id,
      type: exType,
      productId: getProductId(),
      issueKey: issueId,
      fieldValues: [
        ...formatFieldsValue(fields),
        {
          name: 'title',
          label: t("标题"),
          value: `${tapdTitlePrefix}${getTapdTitleKey(type)}${exceptionName}`,
        },
        {
          name: 'description',
          label: t("详细描述"),
          value: t('{{methods}}\nIssue ID：{{issueId}}\n<a href=\'{{url}}&tapd=true\'>&gt;&gt;&gt;点击查看详情&lt;&lt;&lt;</a>', { methods: keyMethods.join('\n'), issueId: rows.issueId, url: getIssueUrl(rows) }).replace(/\n/g, '<br>'),
        },
      ],
    });
    onHide();
  };

  const handleSubmit = async () => {
    if (!params.work_space_id) {
      message.warning(t("请选择TAPD项目"));
      return;
    }
    setLoading(true);
    await Promise.all([
      ...rowsData.map(rows => batchCreateTapd(rows)),
    ]);
    setLoading(false);
    setStatusVisible(false);
    onSubmit();
    reportClick(
      pageId,
      CUSTOM_ACTIONS.commonBatchCreateTapdBug,
      { from: pageId, param1: rowsData.length },
    );
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };

  const loadTapdFields = async (id: string) => {
    setLoading(true);
    const customFields = await getCustomFields(id);
    const newCustomFields = formatCustomFields(customFields, appVersionOptions);
    const tapdTemplateInfos = await getIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: params.work_space_id ?? tapdList[0]?.workspaceId,
    });
    setTapdTemplateInfos(tapdTemplateInfos);
    const tapdInfo = tapdTemplateInfos.find(item => item.templateId === mode) ?? tapdTemplateInfos[0];
    setMode(tapdInfo?.templateId ?? '');
    setIsCreator(tapdInfo?.creator === getUserId());
    storageSet(`${getProductId()}-tapd`, JSON.stringify({ mode, work_space_id: params.work_space_id }));
    if (tapdInfo?.statusRules) {
      setRelateStatus(tapdInfo.statusRules);
    } else {
      setRelateStatus([]);
    }
    const statusFields = newCustomFields.filter((item: CustomFieldsType) => item.name === 'status');
    setStatusFields(statusFields?.[0]);
    const newFields = tapdInfo?.data?.map((item: any) => {
      const findcustom = newCustomFields.find((custom: CustomFieldsType) => custom.name === item.name);
      return ({
        name: item.name,
        value: (item.name === initReporterData.name && !item.value) ? initReporterData.value : item.value,
        label: item.label,
        type: findcustom?.type,
        options: findcustom?.options,
      });
    }).filter((item: any) => item.name !== 'title_prefix');
    const newTitlePrefix = tapdInfo?.data?.find((item: any) => item.name === 'title_prefix')?.value;
    if (newTitlePrefix) {
      setTapdTitlePrefix(newTitlePrefix);
    } else {
      setTapdTitlePrefix(initTitlePrefix);
    }
    if (!!tapdInfo?.data?.length) {
      setFields(newFields!);
    } else {
      setFields(getInitFields(appVersionOptions as FilterOptions[]));
    }
    setLoading(false);
  };

  const renderItem = (field: FieldsType) => {
    switch (field.type) {
      case 'select':
        return <RenderSelect
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'autoComplete':
        return <RenderAutoComplete
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'userSelect':
        return <RenderUserSelect
          name={field.name}
          label={field.label}
          getNewFields={getNewFields}
          value={field.value as string}
        />;
      default:
        return <RenderInput
          name={field.name}
          label={field.label}
          value={field.value}
          getNewFields={getNewFields}
        />;
    }
  };

  useEffect(() => {
    setStatusVisible(visible);
    if (visible) {
      const localTapd = getLocalTapd();
      localTapd?.work_space_id && setParams({ ...params, work_space_id: localTapd.work_space_id });
      localTapd?.mode && setMode(localTapd.mode as string);
      loadIsAdmin();
    }
  }, [visible]);

  useEffect(() => {
    const list: FilterOptions[] = [];
    tapdList.forEach((item) => {
      if (item.workspaceName && item.workspaceId) {
        list.push({ label: item.workspaceName, value: item.workspaceId });
      }
    });
    params.work_space_id = tapdList[0]?.workspaceId || '';
    setTapdLists(list);
  }, [tapdList]);

  useEffect(() => {
    if (params.work_space_id && statusVisible) {
      loadTapdFields(params.work_space_id);
    }
  }, [params.work_space_id, statusVisible, mode]);


  const deleteTemplate =  (item: ITapdTemplateInfos) => {
    setStatusVisible(false);
    ModalMsg({
      type: ModalType.CONFIRM,
      typeIcon: ModalIconType.INFO,
      content: t('你确认要删除缺陷模板 {{name}} ({{id}})吗？', { name: item.templateName, id: item.templateId }),
      cancelText: t("取消"),
      title: t("删除缺陷模板"),
      okText: t("确认"),
      onCancel: () => {
        setStatusVisible(true);
      },
      onOk: () => new Promise<void>((resolve, reject) => {
        deleteIssueTapdInfos({ templateId: item.templateId }).then((rsp) => {
          if (rsp) {
            message.success(t("删除成功!"));
            resolve();
            setTapdTemplateInfos(infos => infos.filter(info => item.templateId !== info.templateId));
            setStatusVisible(true);
          } else {
            reject();
          }
        });
      }),
    });
  };

  const tapdTemplateInfosOptions = () => {
    const userId = getUserId();
    return tapdTemplateInfos.map(item => (
      <SelectOption key={item.templateId} value={item.templateId} label={`${item.templateName} (${item.templateId})`}>
        <div className={styles.templateInfosOptions}>
        <span role="img" aria-label="China">
         {item.templateName} ({item.templateId})
        </span>
          {isAdmin || item.creator === userId ? <CloseLine onClick={(e: { preventDefault: () => void;
            stopPropagation: () => void; }) => {
            e.preventDefault();
            e.stopPropagation();
            deleteTemplate(item);
          }} className={styles.closeIcon}/> : null}
        </div>
      </SelectOption>));
  };

  return (
    <Modal
      visible={statusVisible}
      title={<div style={{
        fontFamily: 'PingFang SC',
        fontWeight: 500,
        fontSize: '16px',
      }}>{t("批量创建缺陷")}</div>}
      centered
      footer={null}
      onCancel={() => {
        setStatusVisible(false);
        onHide();
      }}
      style={{ minWidth: '950px' }}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <RenderSelect
            name={'work_space_id'}
            label={t("关联TAPD")}
            options={tapdLists}
            getNewFields={getNewFields}
            value={params.work_space_id ?? tapdList[0]?.workspaceId}
            handleSelectChange={handleSelectChange}
            labelWidth={'100px'}
            renderSelectWidth={'902px'}
            style={{ width: '794px' }}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <RenderSelect
            name={'mode'}
            label={t("缺陷模板")}
            getNewFields={getNewFields}
            value={mode || tapdTemplateInfos[0]?.templateId || ''}
            handleSelectChange={handleChangeMode}
            labelWidth={'100px'}
            renderSelectWidth={'617px'}
            style={{ width: '507px' }}
            selectProps={{
              optionLabelProp: 'label',
            }}
          >{tapdTemplateInfosOptions()}</RenderSelect>
          <Button
            style={{
              color: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? '#D1D1D4' : '#1573ff',
              textAlign: 'center',
              fontSize: '16px',
              marginLeft: '12px',
              display: 'flex',
              alignItems: 'center',
            }}
            disabled={(!isAdmin && !isCreator) || !tapdTemplateInfos.length}
            onClick={() => {
              if (!params.work_space_id) {
                message.warning(t("请选择TAPD项目，再编辑模板"));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('update', mode || tapdTemplateInfos[0]?.templateId);
              onChangeId(params.work_space_id);
            }}>
            <img src={(!isAdmin && !isCreator) || !tapdTemplateInfos.length ? CreateTapdEditDisable : CreateTapdEdit } style={{ marginRight: '12px', marginBottom: '2px', height: '15px' }} alt={''}/>
            {t("编辑模板")}
          </Button>
          <Button
            style={{
              color: '#0A6CFF',
              textAlign: 'center',
              fontSize: '16px',
              lineHeight: '20px',
              marginLeft: '12px',
            }}
            onClick={() => {
              if (!params.work_space_id) {
                message.warning(t("请选择TAPD项目，再创建新模板"));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('add', '');
              onChangeId(params.work_space_id);
            }}>
            <PlusCircleOutlined style={{ marginRight: '1px' }}/>
            {t("创建新模板")}
          </Button>
        </div>
        <div style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 500, fontFamily: 'PingFang SC', marginTop: '20px' }}>
          {t("基本信息")}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {fields.map(field => renderItem(field))}
        </div>
        <div style={{
          marginBottom: '15px',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'PingFang SC',
          marginTop: '25px',
          // 联动规则不显示，功能暂时不下线
          display: 'none',
        }}>
          {t("Issue处理状态联动")}
          </div>
        <div style={{
          // display: 'flex',
          alignItems: 'flex-end',
          // 联动规则不显示，功能暂时不下线
          display: 'none',
        }}>
          <div>
            {relateStatus.length ? relateStatus.map(item => (
              <div>{t("当TAPD状态为")}<span style={{
                fontWeight: 600,
                display: 'inline-block',
                padding: '0 3px',
              }}>{getTapdStatusName(item.whenTapd, statusFields)}</span>{t("时，Issue状态更改为")}<span style={{
                fontWeight: 600,
                display: 'inline-block',
                padding: '0 3px',
              }}>{item.thenIssue ? StatusMap[item.thenIssue] : ''}</span></div>
            )) : <div>{t("未配置规则")}</div>}
          </div>
          <span
            style={{
              cursor: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? 'not-allowed' : 'pointer',
              color: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? '#D1D1D4' : '#1573ff',
              marginLeft: '10px',
            }}
            onClick={() => {
              if ((!isAdmin && !isCreator) || !tapdTemplateInfos.length) {
                return;
              }
              if (!params.work_space_id) {
                message.warning(t("请选择TAPD项目，再编辑模板"));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('update', mode || tapdTemplateInfos[0]?.templateId);
              onChangeId(params.work_space_id);
            }}>{t("编辑规则")}</span>
        </div>
        <div style={{ textAlign: 'right', marginTop: '40px' }}>
          <Button onClick={() => {
            setStatusVisible(false);
            onHide();
          }}>
            {t("取消")}
          </Button>
          <Button type='primary' onClick={handleSubmit} style={{ marginLeft: '20px' }}>
            {t("确定")}
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};
